import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Check, FileText, Loader2, Printer, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

const kb = (n) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

function Section({ n, title, done, children, testid }) {
  return (
    <section className="border-t border-white/10 py-8" data-testid={testid}>
      <div className="mb-5 flex items-center gap-3">
        <span className={`grid h-8 w-8 place-items-center rounded-full border font-mono text-xs ${done ? "border-emerald-400 text-emerald-300" : "border-white/20 text-slate-300"}`}>
          {done ? <Check className="h-4 w-4" /> : n}
        </span>
        <h3 className="font-display text-xl font-medium">{title}</h3>
      </div>
      {children}
    </section>
  );
}

function FilePicker({ category, label, disabled, onUpload }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    setBusy(true);
    for (const f of files) await onUpload(category, f);
    setBusy(false);
  };
  return (
    <>
      <input ref={ref} type="file" accept="application/pdf,image/jpeg,image/png,image/webp,image/heic" multiple hidden onChange={pick} data-testid={`docs-file-input-${category}`} />
      <button type="button" disabled={disabled || busy} onClick={() => ref.current?.click()} data-testid={`docs-upload-${category}`}
        className="inline-flex items-center gap-2 rounded-full border border-dashed border-white/25 px-5 py-2.5 text-sm transition-colors hover:border-sky-400 hover:bg-white/5 disabled:opacity-40">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {label}
      </button>
    </>
  );
}

function FileList({ files, onRemove }) {
  if (!files.length) return null;
  return (
    <ul className="mt-4 space-y-2">
      {files.map((f) => (
        <li key={f.id} className="flex items-center justify-between gap-3 border border-white/10 bg-[#080B11] px-4 py-2.5 text-sm" data-testid={`docs-file-${f.id}`}>
          <span className="flex min-w-0 items-center gap-2"><FileText className="h-4 w-4 shrink-0 text-sky-400" /><span className="truncate">{f.filename}</span><span className="shrink-0 font-mono text-[11px] text-slate-400">{kb(f.size)}</span></span>
          <button onClick={() => onRemove(f)} className="text-slate-400 hover:text-rose-300" data-testid={`docs-remove-${f.id}`} aria-label="Remove file"><Trash2 className="h-4 w-4" /></button>
        </li>
      ))}
    </ul>
  );
}

export function DocumentsUploader({ reference, token }) {
  const [state, setState] = useState(null);
  const [error, setError] = useState("");
  const [url, setUrl] = useState("");
  const [sig, setSig] = useState({ name: "", title: "", consent: false });
  const [signing, setSigning] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(() => {
    api.get(`/documents/${reference}`, { params: { token } })
      .then((r) => { setState(r.data); setUrl(r.data?.product_url || ""); })
      .catch((e) => setError(errMsg(e)));
  }, [reference, token]);

  useEffect(() => { load(); }, [load]);

  if (error) return <p className="text-rose-300" data-testid="docs-error">{error}</p>;
  if (!state) return <Loader2 className="h-5 w-5 animate-spin text-sky-400" />;

  const docs = Array.isArray(state.documents) ? state.documents : [];
  const byCat = (c) => docs.filter((d) => d.category === c);
  const full = docs.length >= (state.max_documents ?? 6);

  const upload = async (category, file) => {
    const fd = new FormData();
    fd.append("token", token);
    fd.append("category", category);
    fd.append("file", file);
    try {
      const { data } = await api.post(`/documents/${reference}/upload`, fd);
      setState((s) => ({ ...s, documents: [...(s.documents || []), data] }));
      toast.success(`${file.name} uploaded`);
    } catch (e) { toast.error(`${file.name}: ${errMsg(e)}`); }
  };

  const remove = async (f) => {
    try {
      await api.post(`/documents/${reference}/remove/${f.id}`, { token });
      setState((s) => ({ ...s, documents: s.documents.filter((d) => d.id !== f.id) }));
    } catch (e) { toast.error(errMsg(e)); }
  };

  const saveUrl = async () => {
    try {
      const { data } = await api.post(`/documents/${reference}/product-url`, { token, product_url: url });
      setState((s) => ({ ...s, product_url: data.product_url }));
      toast.success("Product page link saved");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const sign = async () => {
    setSigning(true);
    try {
      await api.post(`/documents/${reference}/authorize`, { token, signer_name: sig.name, signer_title: sig.title, consent: sig.consent });
      toast.success("Authorization signed");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSigning(false); }
  };

  const finish = async () => {
    setSubmitting(true);
    try {
      await api.post(`/documents/${reference}/complete`, { token });
      setSubmitted(true);
      toast.success("Documents sent to MidHq");
    } catch (e) { toast.error(errMsg(e)); } finally { setSubmitting(false); }
  };

  const auth = state.authorization;
  return (
    <div data-testid="documents-uploader">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <p className="text-sm text-slate-300">PDF, JPG, PNG, WEBP or HEIC · up to 10 MB each</p>
        <p className={`font-mono text-xs ${full ? "text-amber-300" : "text-slate-400"}`} data-testid="docs-count">{docs.length} / {state.max_documents ?? 6} files</p>
      </div>

      <Section n="1" title="Product page" done={!!state.product_url || byCat("product_page").length > 0} testid="docs-section-product">
        <p className="mb-4 text-sm text-slate-300">Link to the page where you sell, and/or screenshots or PDFs of your product pages.</p>
        <div className="flex flex-col gap-2 sm:flex-row">
          <input className="field" placeholder="https://yourstore.com/product" value={url} onChange={(e) => setUrl(e.target.value)} data-testid="docs-product-url-input" />
          <button onClick={saveUrl} className="btn-ghost shrink-0 justify-center !py-3" data-testid="docs-product-url-save">Save link</button>
        </div>
        <div className="mt-4"><FilePicker category="product_page" label="Upload product page files" disabled={full} onUpload={upload} /></div>
        <FileList files={byCat("product_page")} onRemove={remove} />
      </Section>

      <Section n="2" title="Driver's license" done={byCat("drivers_license").length > 0} testid="docs-section-license">
        <p className="mb-4 text-sm text-slate-300">A clear photo or scan of the front and back of the business owner's driver's license.</p>
        <FilePicker category="drivers_license" label="Upload driver's license" disabled={full} onUpload={upload} />
        <FileList files={byCat("drivers_license")} onRemove={remove} />
      </Section>

      <Section n="3" title="Credit check authorization" done={!!auth || byCat("credit_authorization").length > 0} testid="docs-section-credit">
        {auth ? (
          <p className="text-sm text-emerald-300" data-testid="docs-auth-signed">Signed by {auth.signer_name}{auth.signer_title ? `, ${auth.signer_title}` : ""} on {new Date(auth.signed_at).toLocaleString()}.</p>
        ) : (
          <div className="space-y-4">
            <div className="max-h-44 overflow-y-auto border border-white/10 bg-[#080B11] p-4 text-sm leading-relaxed text-slate-300" data-lenis-prevent data-testid="docs-auth-text">{state.credit_auth_text}</div>
            <div className="grid gap-3 sm:grid-cols-2">
              <input className="field" placeholder="Type your full legal name *" value={sig.name} onChange={(e) => setSig({ ...sig, name: e.target.value })} data-testid="docs-auth-name" />
              <input className="field" placeholder="Title (e.g. Owner, CEO)" value={sig.title} onChange={(e) => setSig({ ...sig, title: e.target.value })} data-testid="docs-auth-title" />
            </div>
            <label className="flex cursor-pointer items-start gap-3 text-sm text-slate-200">
              <input type="checkbox" className="mt-1 h-4 w-4 accent-[#F59E0B]" checked={sig.consent} onChange={(e) => setSig({ ...sig, consent: e.target.checked })} data-testid="docs-auth-consent" />
              I have read and agree to the authorization above, and typing my name serves as my electronic signature.
            </label>
            <button onClick={sign} disabled={signing || !sig.consent || sig.name.trim().length < 3} className="btn-primary" data-testid="docs-auth-sign">
              {signing && <Loader2 className="h-4 w-4 animate-spin" />} Sign authorization
            </button>
          </div>
        )}
        <div className="mt-6 border-t border-dashed border-white/10 pt-5">
          <p className="mb-3 text-sm text-slate-400">Prefer paper? Print the form, sign it and upload the signed copy.</p>
          <div className="flex flex-wrap gap-2">
            <Link to="/credit-authorization-form" target="_blank" className="inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 text-sm hover:border-sky-400" data-testid="docs-print-form"><Printer className="h-4 w-4" /> Print blank form</Link>
            <FilePicker category="credit_authorization" label="Upload signed form" disabled={full} onUpload={upload} />
          </div>
          <FileList files={byCat("credit_authorization")} onRemove={remove} />
        </div>
      </Section>

      <div className="border-t border-white/10 pt-8">
        {submitted ? (
          <p className="text-sm text-emerald-300" data-testid="docs-submitted">Thanks. Your documents were sent to our underwriting team. You can still add files with this link if we ask for more.</p>
        ) : (
          <>
            <p className="mb-4 text-sm text-slate-300">Done uploading? Let our underwriting team know your file is ready.</p>
            <button onClick={finish} disabled={submitting || (!docs.length && !auth && !state.product_url)} className="btn-primary" data-testid="docs-submit-button">
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />} Submit documents to MidHq
            </button>
          </>
        )}
      </div>
    </div>
  );
}
